/**
 * MeslekiEgitimKartlari.jsx — Kişi Detay › İSG Belgeleri › Meslek alt sekmesindeki Mesleki Eğitim kartları (backlog #372,
 * backend #371).
 *
 * Kişi bir fuarda birden çok meslek taşıyorsa (`meslekiEgitimler[]`) HER MESLEĞİN kendi sertifikası vardır: bir kart,
 * kendi Yükle / İncele düğmeleriyle (yükleme o mesleğe etiketlenir). İstenmeyen meslek kartı soluk gelir ve yargılanmaz.
 * Yuva listesi yoksa (fuarsız / eski yanıt) tek eski kart.
 *
 * Altta, 2+ meslek sertifika isterken HANGİSİ olduğu söylenmemiş sertifikalar ("etiketsiz", hiçbir mesleğe sayılmaz): her
 * birinin yanında "hangi meslek için?" listesi ve Ata düğmesi. Atama (`tasi { meslekId }`) incelemeyi sıfırlar — incelenmiş
 * belgede bedeli önceden söylenir.
 *
 * @param yuvalar     `meslekiEgitimler[]` ya da null
 * @param etiketsiz   `etiketsizMeslekiEgitimBelgeleri[]`
 * @param tekDoc      yuva yokken tek kartın belgesi (`documents.meslekiEgitim`)
 * @param tekIstenen  yuva yokken mesleğin kuralı belgeyi istiyor mu
 * @param vurgu       Kişi Listesi'nden gri kareyle gelindi (#273) — ilk kart vurgulanır
 * @param onIncele    (doc) => void
 * @param onYukle     ({ meslekId, meslekAdi } | null) => void — null: etiketsiz (eski) yükleme; verilmezse yükleme yok
 * @param onEtiketle  (fileId, meslekId) => Promise — etiketsiz sertifikayı bir mesleğe atar; verilmezse atama yok
 *
 * `EtiketsizSertifikaKarti` ayrıca dışa açık: firma portalı (çevrilmiş metinlerle, `etiket` + `dosyaAc`) aynı kartı kullanır.
 */
import React, { useState } from "react";
import { Badge, Button, Card, CardBody, CardHeader, Input, Spinner } from "reactstrap";
import { AlertTriangle } from "lucide-react";
import MeslekiEgitimKarti from "./MeslekiEgitimKarti";
import { FILE_APPROVAL_STATUS } from "../../services/projectService";

const ETIKET_TR = {
  baslik: "Hangi mesleğe ait olduğu belirtilmemiş sertifikalar",
  incele: "İncele",
  hangiMeslek: "Hangi meslek için?",
  ata: "Ata",
  sonKullanma: "Son kullanma",
  sifirlanir: "Atandığında incelemesi sıfırlanır; belge yeniden Beklemede'ye döner.",
};

function EtiketsizSatir({ belge, secenekler, onIncele, onEtiketle, dosyaAc, etiket }) {
  const [meslekId, setMeslekId] = useState("");
  const [busy, setBusy] = useState(false);
  const info = belge.status != null ? FILE_APPROVAL_STATUS[belge.status] : null;
  // Etiketleme bir taşımadır: inceleme sıfırlanır (incelenmiş belge kararını kaybeder, firmanınki Beklemede'ye döner).
  const incelendi = belge.reviewStatus != null;

  async function ata() {
    setBusy(true);
    try {
      await onEtiketle(belge.fileId, Number(meslekId));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="py-2 border-top">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="small text-break">{belge.fileName ?? "Dosya"}</span>
        {info && <Badge color={info.color} className="fw-normal">{info.label}</Badge>}
        {belge.expiryDate && <span className="small text-muted">{etiket.sonKullanma} {belge.expiryDate}</span>}
        <span className="ms-auto">
          {dosyaAc ? dosyaAc(belge) : <Button color="light" size="sm" onClick={() => onIncele(belge)}>{etiket.incele}</Button>}
        </span>
      </div>
      {onEtiketle && (
        <div className="d-flex align-items-center gap-2 mt-2">
          <Input type="select" bsSize="sm" value={meslekId} disabled={busy} onChange={(e) => setMeslekId(e.target.value)}>
            <option value="">{etiket.hangiMeslek}</option>
            {secenekler.map((y) => <option key={y.meslekId} value={String(y.meslekId)}>{y.meslekAdi}</option>)}
          </Input>
          <Button color="primary" size="sm" disabled={!meslekId || busy} onClick={ata}>
            {busy ? <Spinner size="sm" /> : etiket.ata}
          </Button>
        </div>
      )}
      {onEtiketle && incelendi && <div className="small text-muted mt-1">{etiket.sifirlanir}</div>}
    </div>
  );
}

/**
 * Etiketsiz sertifikaların kartı. `secenekler`: atanabilecek meslekler (`{ meslekId, meslekAdi }`, yalnızca sertifika isteyenler);
 * `etiket`: görünen metinler (varsayılan Türkçe); `dosyaAc(belge)`: verilirse İncele düğmesinin yerine geçen düğüm
 * (firma portalı dosyayı yalnızca açar, incelemez).
 */
export function EtiketsizSertifikaKarti({ belgeler, secenekler, onIncele, onEtiketle, dosyaAc, etiket = ETIKET_TR, className = "mb-0" }) {
  if (!belgeler?.length) return null;
  return (
    <Card className={`${className} border-warning`}>
      <CardHeader className="bg-transparent d-flex align-items-center gap-2">
        <AlertTriangle size={16} className="text-warning" />
        <span className="fw-semibold">{etiket.baslik}</span>
      </CardHeader>
      <CardBody className="pt-2 pb-2">
        {belgeler.map((b) => (
          <EtiketsizSatir
            key={b.fileId} belge={b} secenekler={secenekler} onIncele={onIncele} dosyaAc={dosyaAc} etiket={etiket}
            onEtiketle={secenekler.length > 0 ? onEtiketle : undefined}
          />
        ))}
      </CardBody>
    </Card>
  );
}

export default function MeslekiEgitimKartlari({
  yuvalar, etiketsiz = [], tekDoc, tekIstenen, vurgu = false, onIncele, onYukle, onEtiketle,
}) {
  if (!yuvalar) {
    return (
      <MeslekiEgitimKarti
        doc={tekDoc} istenen={tekIstenen} vurgu={vurgu}
        onIncele={() => onIncele(tekDoc)} onYukle={onYukle ? () => onYukle(null) : undefined}
      />
    );
  }
  const cok = yuvalar.length > 1;
  const secenekler = yuvalar.filter((y) => y.istenen);
  return (
    <div className="vstack gap-3">
      {yuvalar.map((y, i) => (
        <MeslekiEgitimKarti
          key={y.meslekId}
          id={i === 0 ? "meslek-egitim-karti" : `meslek-egitim-karti-${y.meslekId}`}
          doc={y.belge} istenen={y.istenen} sayilir={y.sayilir} meslekAdi={cok ? y.meslekAdi : null}
          vurgu={vurgu && i === 0}
          onIncele={() => onIncele(y.belge)}
          onYukle={onYukle ? () => onYukle({ meslekId: y.meslekId, meslekAdi: y.meslekAdi }) : undefined}
        />
      ))}
      <EtiketsizSertifikaKarti belgeler={etiketsiz} secenekler={secenekler} onIncele={onIncele} onEtiketle={onEtiketle} />
    </div>
  );
}
